import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { StyledContainer, DiffModeButton } from "./styles.js";
import StyledSelect from "../StyledSelect/index.js";
import { useParams } from "react-router-dom";
import ReactTextareaAutosize from "react-textarea-autosize";
import Auth from "../../Auth.js";
// import MergelyView from "mergely-react";
// import _ from "lodash";
import { useImmer } from "use-immer";
import DiffMatchPatchViewer from "./DiffMatchPatchViewer.js";
import useScript from "react-script-hook/lib/use-script.js";

const DIFF_MODES = {
  ORIGINAL: "original",
  INLINE: "inline",
  REVISED: "revised",
  SIDE_BY_SIDE: "side-by-side",
};

const MODELS = [
  { label: "DeepSeek Instant", value: "rewriteDeepSeekDistilled" },
  { label: "o1", value: "rewriteO1" },
  { label: "o1-mini", value: "rewriteO1Mini" },
  { label: "DeepSeek r1", value: "rewriteDeepSeek" },
  { label: "GPT-4o", value: "rewriteGpt4o" },
  { label: "Claude 3.5 Sonnet", value: "rewriteClaude35Sonnet" },
];

export default function Reviser({}) {
  const { projectId } = useParams();
  const confirmAction = useConfirmation(projectId);

  const [loaded, setLoaded] = useState(false);

  const [loading, _] = useScript({
    src: "https://cdnjs.cloudflare.com/ajax/libs/mergely/5.0.0/mergely.min.js",
    // checkForExisting: true,
    onload: () => {
      setLoaded(true);
    },
  });

  const [original, setOriginal] = useState(null);
  const [revised, setRevised] = useState(null);

  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingTime, setStreamingTime] = useState(0);
  const [isStreamingTodos, setIsStreamingTodos] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [todos, setTodos] = useImmer([]);

  const [activeTab, setActiveTab] = useState("customText");
  const [customText, setCustomText] = useState("");
  const customTextRef = useRef(null);
  const [customTextHistory, setCustomTextHistory] = useState([]);
  const [customTextHistoryIndex, setCustomTextHistoryIndex] = useState(-1);
  const [wordCount, setWordCount] = useState(0);
  const [diffMode, setDiffMode] = useState("original");
  const [selectedModel, setSelectedModel] = useState(MODELS[0]);
  const [error, setError] = useState(null);

  // Add this useEffect to handle the error timeout
  useEffect(() => {
    let timer;
    if (error) {
      timer = setTimeout(() => {
        setError(null);
      }, 5000); // 5000 milliseconds = 5 seconds
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [error]);

  useEffect(() => {
    const original = localStorage.getItem(`reviser-${projectId}-original`);
    const revised = localStorage.getItem(`reviser-${projectId}-revised`);

    if (original) {
      setOriginalState(original);
    }
    if (revised) {
      setRevisedState(revised);
    }
  }, []);

  useEffect(() => {
    let interval;
    if (isStreaming) {
      interval = setInterval(() => {
        setStreamingTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      setStreamingTime(0);
    }
    return () => clearInterval(interval);
  }, [isStreaming]);

  useEffect(() => {
    const savedInstructions = localStorage.getItem(`reviser-${projectId}-instructions`);
    if (savedInstructions) {
      setInstructions(savedInstructions);
    }
    const savedTodos = localStorage.getItem(`reviser-${projectId}-todos`);
    if (savedTodos) {
      setTodos(JSON.parse(savedTodos));
    }
  }, [projectId]);

  useEffect(() => {
    localStorage.setItem(`reviser-${projectId}-instructions`, instructions);
  }, [projectId, instructions]);

  useEffect(() => {
    localStorage.setItem(`reviser-${projectId}-todos`, JSON.stringify(todos));
  }, [projectId, todos]);

  let originalWithoutTags = original?.replace(/<\/?[^>]+(>|$)/g, "");

  const prevDiffModeRef = useRef(diffMode);

  const setRevisedState = useCallback(
    (value) => {
      if (diffMode === DIFF_MODES.SIDE_BY_SIDE) {
        mergelyRef.current.rhs(value);
      } else {
        setRevised(value);
      }

      localStorage.setItem(`reviser-${projectId}-revised`, value);
    },
    [diffMode]
  );

  const setOriginalState = useCallback(
    (value) => {
      if (diffMode === DIFF_MODES.SIDE_BY_SIDE) {
        mergelyRef.current.lhs(value);
      } else {
        setOriginal(value);
      }

      localStorage.setItem(`reviser-${projectId}-original`, value);
    },
    [diffMode]
  );

  const handleRewrite = useCallback(
    async e => {
      e.preventDefault();
      if (!instructions || instructions.trim() === "") {
        return alert("Please enter instructions to rewrite.");
      }

      // Add confirmation dialog
      const runConfirmedAction = async () => {
        setIsStreaming(true);
        setStreamingTime(0);
        setError(null); // Clear previous errors

        const url = `/api/ai/runDynamicPluginAction/reviserPlugins/${selectedModel.value}`;
        const pluginId = `reviserPlugins/${selectedModel.value}`;
        setRevisedState("");
        let localOutline = "";
        const combinedInstructions = todos.length > 0 ? `${instructions}\n\nTodos:\n${todos.join("\n")}` : instructions;
        await Auth.streamWithIdToken(url, {
          pluginId,
          params: {
            instructions: combinedInstructions,
            content: original,
            projectId,
            documentId: "29JZ9QMm8unCa5Z6zWdW",
            type: activeTab
          }
        }, null, chunk => {
          if (chunk.content) {
            localOutline += chunk.content.text;
          }
        }, () => {
          setRevisedState(localOutline);
          setIsStreaming(false);
          if (diffMode === DIFF_MODES.ORIGINAL || diffMode === DIFF_MODES.REVISED) {
            setDiffMode(DIFF_MODES.INLINE);
          }
        }, error => {
          setIsStreaming(false);
          setError(`Error: ${error.message || "An unknown error occurred"}`);
        });
      };
      if (revised && revised.trim() !== "") {
        const wordCount = revised.trim().split(/\s+/).length;
        const confirmMessage = `This will overwrite the revised version, which has ${wordCount} words. Are you sure?`;
        await confirmAction({
          title: "Overwrite revised version",
          message: confirmMessage,
          actionLabel: "Overwrite",
          destructive: true
        }, runConfirmedAction);
      } else {
        await runConfirmedAction();
      }
    },
    [projectId, instructions, todos, original, diffMode, selectedModel, revised, confirmAction]
  );

  const getTodos = useCallback(
    async (e) => {
      e.preventDefault();

      if (!instructions || instructions.trim() === "") {
        return alert("Please enter instructions to generate todos.");
      }

      setIsStreamingTodos(true);

      const url = `/api/ai/runDynamicPluginAction/reviserPlugins/todo`;
      const pluginId = `reviserPlugins/todo`;

      let todoContent = "";

      await Auth.streamWithIdToken(
        url,
        {
          pluginId,
          params: {
            instructions,
            content: originalWithoutTags,
            type: activeTab,
            documentId: "29JZ9QMm8unCa5Z6zWdW",
          },
        },
        null,
        (chunk) => {
          if (chunk.content) {
            todoContent += chunk.content.text;
          }
        },
        () => {
          setIsStreamingTodos(false);
          parseTodos(todoContent);
        },
        (chunk) => {
          setIsStreamingTodos(false);
        }
      );
    },
    [projectId, instructions, original, originalWithoutTags]
  );

  const parseTodos = (content) => {
    const todoRegex = /(\d+\.)\s*([\s\S]*?)(?=\n\d+\.|\n*$)/g;
    const parsedTodos = [];
    let match;

    while ((match = todoRegex.exec(content)) !== null) {
      parsedTodos.push(match[2].trim());
    }

    setTodos((draft) => {
      draft.push(...parsedTodos);
    });
  };

  const handleTodoChange = useCallback((index, newValue) => {
    setTodos((draft) => {
      draft[index] = newValue;
    });
  }, []);

  const handleTodoDelete = useCallback((index) => {
    setTodos((draft) => {
      draft.splice(index, 1);
    });
  }, []);

  const handleAddTodo = useCallback(() => {
    setTodos((draft) => {
      draft.push("New todo item");
    });
  }, []);

  const handleClearAllTodos = useCallback(() => {
    setTodos([]);
  }, []);
  const handleAddComment = useCallback(
    (e) => {
      e.preventDefault();
      const textarea = customTextRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        if (start !== end) {
          const selectedText = customText.substring(start, end);
          const newText = customText.substring(0, start) + `<note text="">` + selectedText + `</note>` + customText.substring(end);
          setCustomText(newText);
          setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + 12, start + 12);
            const commentStart = textarea.value.lastIndexOf("<note", start);
            if (commentStart !== -1) {
              textarea.scrollTop = textarea.scrollHeight;
              const lineHeight = parseInt(getComputedStyle(textarea).lineHeight);
              const lines = textarea.value.substr(0, commentStart).split("\n").length;
              textarea.scrollTop = lines * lineHeight - textarea.clientHeight / 2;
            }
          }, 0);
        }
      }
    },
    [customText]
  );

  const handleCustomTextChange = useCallback(
    (e) => {
      const newText = e.target.value;
      setCustomText(newText);
      setCustomTextHistory((prevHistory) => [...prevHistory.slice(0, customTextHistoryIndex + 1), newText]);
      setCustomTextHistoryIndex((prevIndex) => prevIndex + 1);
      localStorage.setItem("customText", newText);
      setWordCount(newText.trim().split(/\s+/).length);
    },
    [customTextHistoryIndex]
  );

  const handleCustomTextKeyDown = useCallback(
    (e) => {
      if (e.metaKey && e.key === "z") {
        e.preventDefault();
        if (customTextHistoryIndex > 0) {
          setCustomTextHistoryIndex((prevIndex) => prevIndex - 1);
          const previousText = customTextHistory[customTextHistoryIndex - 1];
          setCustomText(previousText);
          localStorage.setItem("customText", previousText);
          setWordCount(previousText.trim().split(/\s+/).length);
        }
      }
    },
    [customTextHistory, customTextHistoryIndex]
  );

  const mergelyRef = useRef(null);
  const mergelyContainerRef = useRef(null);

  useEffect(() => {
    // console.log("diffMode in effect", diffMode);
    // console.log("prevDiffModeRef.current", prevDiffModeRef.current);
    if (diffMode !== prevDiffModeRef.current) {
      prevDiffModeRef.current = diffMode;

      if (diffMode === DIFF_MODES.SIDE_BY_SIDE) {
        if (mergelyContainerRef.current) {
          const alreadyInitialized = !!mergelyRef.current;

          if (!alreadyInitialized) {
            mergelyRef.current = new window.Mergely("#mergely-container", {
              wrap_lines: true,
              ignorews: true,
            });
          }

          const doc = mergelyRef.current;

          if (alreadyInitialized) {
            doc.lhs(original);
            doc.rhs(revised);
          } else {
            doc.once("updated", () => {
              if (original) {
                doc.lhs(original);
              }
              if (revised) {
                doc.rhs(revised);
              }

              doc.on("changed", () => {
                if (diffMode === DIFF_MODES.SIDE_BY_SIDE) {
                  setOriginal(doc.get("lhs"));
                  setRevised(doc.get("rhs"));

                  localStorage.setItem(`reviser-${projectId}-original`, doc.get("lhs"));
                  localStorage.setItem(`reviser-${projectId}-revised`, doc.get("rhs"));
                }
              });
            });
          }
        }
      }
    }
  }, [diffMode, original, revised]);

  useEffect(() => {
    if (diffMode === DIFF_MODES.SIDE_BY_SIDE) {
      if (mergelyRef.current && revised?.trim() === "") {
        const mergelyContainer = document.getElementById("mergely-container");
        if (mergelyContainer) {
          const style = document.createElement("style");
          style.textContent =
            ".mergely-editor span { text-decoration: none !important; color: black !important; }  .mergely-editor .mergely.lhs.d.CodeMirror-linebackground { display: none !important; }";
          mergelyContainer.appendChild(style);
        }
      }

      if (mergelyRef.current && revised?.trim() !== "") {
        const mergelyContainer = document.getElementById("mergely-container");
        if (mergelyContainer) {
          const style = mergelyContainer.querySelector("style");
          if (style) {
            mergelyContainer.removeChild(style);
          }
        }
      }
    }
  }, [revised]);

  const isLongText = original && original.trim().split(/\s+/).length > 3000;
  const isWeakerModel = !["rewriteO1", "rewriteO1Mini", "rewriteDeepSeek"].includes(selectedModel.value);

  if (!loaded) {
    return (
      <StyledContainer>
        <h1>Loading...</h1>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <h1>👷🏻‍♀️ Reviser</h1>

      <div className="reviser-content">
        <nav>
          <div className="scroll-left">
            <ReactTextareaAutosize
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder={`Overall instructions for how to rewrite the original.\n\nExample: Add a more striking opening image which is mirrored in the ending`}
              className="instructions"
            />
            <div className="button-group">
              <label>Model:</label>
              <StyledSelect
                value={selectedModel}
                options={MODELS}
                onChange={(value) => setSelectedModel(value)}
              >
                {MODELS.map((model) => (
                  <option
                    key={model.value}
                    value={model.value}
                  >
                    {model.label}
                  </option>
                ))}
              </StyledSelect>
              <button
                onClick={handleRewrite}
                disabled={isStreaming}
              >
                {isStreaming ? `Rewriting... (${streamingTime}s)` : "✨ Rewrite"}
              </button>

              {isStreaming && (
                <>
                  <br />
                  <br />
                  <small>Be patient. This can take up to 5 minutes. (For DeepSeek, 10 minutes)</small>
                </>
              )}

              {!isStreaming && isLongText && isWeakerModel && (
                <div className="warning-message">
                  <small>Warning: The selected model may not be adequate for texts longer than 3000 words. Consider using o1 or o1-mini for better results.</small>
                </div>
              )}
            </div>
            <div className="todos">
              <h2>Todos</h2>
              <small>Todos are specific tasks that give more detail on how to rewrite the Original. These can also be generated using your instructions above.</small>
              <div className="todo-buttons">
                <button onClick={handleAddTodo}>+ Todo</button>
                {todos.length > 0 && <button onClick={handleClearAllTodos}>Clear All</button>}

                <button
                  disabled={isStreamingTodos}
                  onClick={getTodos}
                >
                  {isStreamingTodos ? "Generating..." : "✨ Generate Todos"}
                </button>
              </div>
              {todos.length > 0 && (
                <ul>
                  {todos.map((todo, index) => (
                    <li key={index}>
                      <ReactTextareaAutosize
                        value={todo}
                        onChange={(e) => handleTodoChange(index, e.target.value)}
                      />
                      <a onClick={() => handleTodoDelete(index)}>🗑️</a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </nav>

        <div className="main">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <span>Original: {original ? original.trim().split(/\s+/).length : 0} words</span>
            <div className="diff-mode-toggle">
              <DiffModeButton
                onClick={() => setDiffMode(DIFF_MODES.ORIGINAL)}
                active={diffMode === DIFF_MODES.ORIGINAL}
              >
                Original
              </DiffModeButton>
              <DiffModeButton
                onClick={() => setDiffMode(DIFF_MODES.REVISED)}
                active={diffMode === DIFF_MODES.REVISED}
              >
                Revised
              </DiffModeButton>

              <DiffModeButton
                onClick={() => setDiffMode(DIFF_MODES.INLINE)}
                active={diffMode === DIFF_MODES.INLINE}
              >
                Inline Diff
              </DiffModeButton>

              <DiffModeButton
                onClick={() => setDiffMode(DIFF_MODES.SIDE_BY_SIDE)}
                active={diffMode === DIFF_MODES.SIDE_BY_SIDE}
              >
                Side-by-Side Diff
              </DiffModeButton>
            </div>
            <span>Revised: {revised ? revised.trim().split(/\s+/).length : 0} words</span>
          </div>

          {diffMode === DIFF_MODES.ORIGINAL && (
            <div className="original-container">
              <ReactTextareaAutosize
                value={original}
                onChange={(e) => setOriginalState(e.target.value)}
                className="original"
              />
            </div>
          )}

          {diffMode === DIFF_MODES.REVISED && (
            <div className="original-container">
              <ReactTextareaAutosize
                value={revised}
                onChange={(e) => setRevisedState(e.target.value)}
                className="revised"
              />
            </div>
          )}

          {original && revised && diffMode === DIFF_MODES.INLINE && (
            <div className="diff-match-patch-viewer inline-container">
              <DiffMatchPatchViewer
                string1={original}
                string2={revised}
              />
            </div>
          )}
          <div
            id="mergely-container"
            style={{
              height: "95%",
              display: diffMode === DIFF_MODES.SIDE_BY_SIDE ? "block" : "none",
            }}
            ref={mergelyContainerRef}
          ></div>
        </div>
      </div>

      {error && <div id="error">{error}</div>}
    </StyledContainer>
  );
}
